"use client";

import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Wine, MapPin, Euro } from "lucide-react";

function SingleWineContent() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div className="min-h-screen bg-background py-12 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="h-12 w-48 rounded-md bg-muted/30 animate-pulse mb-8" />
          <div className="grid gap-12 lg:grid-cols-2">
            <div className="aspect-square rounded-lg bg-muted/30 animate-pulse" />
            <div className="space-y-6">
              <div className="h-8 w-3/4 rounded-md bg-muted/30 animate-pulse" />
              <div className="h-24 w-full rounded-md bg-muted/30 animate-pulse" />
              <div className="h-10 w-32 rounded-md bg-muted/30 animate-pulse" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !posts || posts.length === 0) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <div className="text-center space-y-4">
          <Wine className="mx-auto size-16 text-muted-foreground" />
          <h2 className="text-2xl font-display font-bold text-foreground">
            Vino no encontrado
          </h2>
          <p className="text-muted-foreground">
            Lo sentimos, no pudimos encontrar este vino.
          </p>
          <Button asChild>
            <Link to="/HomePage">Volver al inicio</Link>
          </Button>
        </div>
      </div>
    );
  }

  const wine = posts[0];
  const customFields = wine.customFields || {};

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto max-w-6xl px-4 py-12">
        <Link
          to="/HomePage"
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-8"
        >
          ← Volver a la lista de vinos
        </Link>

        <div className="grid gap-12 lg:grid-cols-2">
          <div className="space-y-6">
            {wine.featuredImage ? (
              <div className="aspect-square rounded-lg overflow-hidden border border-border shadow-soft">
                <img
                  src={wine.featuredImage}
                  alt={customFields.name || wine.title}
                  className="size-full object-cover hover:scale-105 transition-transform duration-[var(--duration-slow)] ease-[var(--ease-out-back)]"
                />
              </div>
            ) : (
              <div className="aspect-square rounded-lg bg-muted/30 flex items-center justify-center border border-border">
                <Wine className="size-24 text-muted-foreground" />
              </div>
            )}
          </div>

          <div className="space-y-6">
            <div>
              {customFields.type && (
                <Badge variant="secondary" className="mb-4">
                  {customFields.type}
                </Badge>
              )}
              <h1 className="text-4xl lg:text-5xl font-display font-bold text-foreground mb-4">
                {customFields.name || wine.title}
              </h1>
              {customFields.region && (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <MapPin className="size-4" />
                  <span className="text-base">{customFields.region}</span>
                </div>
              )}
            </div>

            {customFields.price !== undefined && customFields.price !== "" && (
              <div className="flex items-baseline gap-2">
                <Euro className="size-6 text-primary" />
                <span className="text-3xl font-bold text-foreground">
                  {customFields.price}
                </span>
                <span className="text-muted-foreground">EUR</span>
              </div>
            )}

            <Separator />

            {customFields.description && (
              <div className="space-y-3">
                <h2 className="text-xl font-display font-semibold text-foreground">
                  Descripción
                </h2>
                <p className="text-muted-foreground leading-relaxed whitespace-pre-wrap">
                  {customFields.description}
                </p>
              </div>
            )}

            {wine.content && (
              <div className="space-y-3">
                <h2 className="text-xl font-display font-semibold text-foreground">
                  Detalles
                </h2>
                <div
                  className="text-muted-foreground leading-relaxed prose prose-sm max-w-none"
                  dangerouslySetInnerHTML={{ __html: wine.content }}
                />
              </div>
            )}

            <div className="pt-6 flex gap-3">
              <Button asChild size="lg">
                <Link to="/HomePage">Ver más vinos</Link>
              </Button>
              <Button asChild variant="outline" size="lg">
                <Link to="/HomePage">Contactar</Link>
              </Button>
            </div>
          </div>
        </div>

        {wine.tags && wine.tags.length > 0 && (
          <div className="mt-12 pt-8 border-t border-border">
            <h3 className="text-lg font-display font-semibold text-foreground mb-4">
              Características
            </h3>
            <div className="flex flex-wrap gap-2">
              {wine.tags.map((tag) => (
                <Badge key={tag.id} variant="outline">
                  {tag.name}
                </Badge>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function SingleWineTemplateSection() {
  const currentPostId = (window as any).wvcClient?.getCurrentPostID();

  return (
    <section id="single-wine-template-section">
      <PostsProvider postType="wine" wp_query={{ include: [currentPostId], per_page: 1 }} embeds={["wp:featuredmedia", "wp:term"]} fields={["id", "title.rendered", "content.rendered", "excerpt.rendered", "date", "tags", "link", "_embedded", "featured_media", "meta", "_links.wp:featuredmedia", "_links.wp:term"]}>
        <SingleWineContent />
      </PostsProvider>
    </section>
  );
}